import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  editMeeting as editMeetingAPI,
  type MeetingEditPayload,
} from '@/api/v4/meetings.api';
import { notify } from '@/ui/snackbar/notify';
import { QueryKey } from '@/config/query-client';
import { useCompanyFetchMode } from '@/features/companies/overview/company-fetch-context';

type EditMeetingVariables = {
  payload: MeetingEditPayload;
  previousOrganizationId?: string | null;
};

export const useEditMeeting = () => {
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();
  const fetchMode = useCompanyFetchMode();

  const editMeetingMutation = useMutation({
    mutationFn: ({ payload }: EditMeetingVariables) => editMeetingAPI(payload),
    onSuccess: async (meeting, { previousOrganizationId }) => {
      const organizationIds = [
        ...new Set(
          [meeting.organizationId, previousOrganizationId].filter(
            (organizationId): organizationId is string =>
              typeof organizationId === 'string' && organizationId.length > 0,
          ),
        ),
      ];

      await Promise.all([
        ...organizationIds.flatMap(organizationId => [
          queryClient.invalidateQueries({
            queryKey: [QueryKey.Company, fetchMode, organizationId],
          }),
          queryClient.invalidateQueries({
            queryKey: [QueryKey.CompanyMeetings, organizationId],
          }),
        ]),
        queryClient.invalidateQueries({ queryKey: [QueryKey.LastActivities] }),
        queryClient.invalidateQueries({
          queryKey: [QueryKey.LastActivitiesCount],
        }),
        ...(meeting.projectId
          ? [
              queryClient.invalidateQueries({
                queryKey: [QueryKey.Project, meeting.projectId],
              }),
              queryClient.invalidateQueries({
                queryKey: [QueryKey.ProjectMeetings, meeting.projectId],
              }),
            ]
          : []),
        ...(meeting.teamId
          ? [
              queryClient.invalidateQueries({
                queryKey: [QueryKey.Team, meeting.teamId],
              }),
              queryClient.invalidateQueries({
                queryKey: [QueryKey.TeamMeetings, meeting.teamId],
              }),
            ]
          : []),
      ]);
    },
    onError: (error: Error) => {
      notify({
        message: t`unknownError`,
      });
      console.error(error.message);
    },
  });

  return {
    editMeeting: editMeetingMutation.mutateAsync,
    isError: editMeetingMutation.isError,
  };
};
